import React, { useState } from 'react';
import { Calendar, Clock, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { DayShift } from '@/types';
import { format } from 'date-fns';
import {
  safeParseDate,
  calculateTotalDuration,
  formatDuration,
  formatShiftTimeRange
} from '@/lib/shifts/template-utils';
import BaseTemplateDialog from './shared/BaseTemplateDialog';

interface SaveDayTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description: string) => Promise<void>;
  dayShifts: DayShift[];
  selectedDate: Date;
}

const SaveDayTemplateDialog: React.FC<SaveDayTemplateDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  dayShifts,
  selectedDate,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (name: string, description?: string) => {
    setIsSaving(true);
    try {
      await onSave(name, description || '');
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate totals
  const totalDuration = calculateTotalDuration(dayShifts);
  const formattedDuration = formatDuration(totalDuration);
  const assignedShifts = dayShifts.filter(shift => shift.employee_id).length;

  return (
    <BaseTemplateDialog
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      title="Save Day Template"
      icon={Calendar}
      saveButtonText="Save Day Template"
      isLoading={isSaving}
    >
      {/* Day Template Preview */}
      <div className="text-sm max-h-48 overflow-y-auto">
        <div className="font-medium mb-2 flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Day Template for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
        </div>

        <div className="space-y-2 mb-3">
          <div className="text-xs text-muted-foreground">
            {dayShifts.length} shift{dayShifts.length !== 1 ? 's' : ''} • {assignedShifts} assigned • Total: {formattedDuration}
          </div>
        </div>

        <div className="space-y-2">
          {dayShifts.map((shift) => {
            const startTime = safeParseDate(shift.start_time);
            const endTime = safeParseDate(shift.end_time);
            const duration = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60 * 100)) / 100;

            return (
              <Card key={shift.id} className="p-2">
                <CardContent className="p-0">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-mono">
                        {formatShiftTimeRange(shift)}
                      </span>
                      <span className="text-muted-foreground">
                        ({duration}h)
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>{shift.employee_id ? 'Assigned' : 'Unassigned'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-2 text-muted-foreground text-xs">
          This template can be reused to quickly create similar daily schedules.
        </div>
      </div>
    </BaseTemplateDialog>
  );
};

export default SaveDayTemplateDialog;